import { DOCUMENT } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { MatButton } from '@angular/material/button';

/**
 * Set on the document root to the bar's height while it shows, and removed
 * when it goes. Anything pinned to the bottom of the viewport sits at
 * `bottom: var(--impersonation-bar-height, 0px)` so the bar never covers it.
 */
export const IMPERSONATION_BAR_HEIGHT_VAR = '--impersonation-bar-height';

/** Milliseconds as MM:SS; 00:00 once time is up. */
export const formatTimeLeft = (ms: number | null | undefined): string => {
  if (!ms || ms <= 0) return '00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/**
 * EXT-6617: the red bar shown while a Jim's employee is signed in as someone
 * else, in both ecommerce and dealer-portal. It's fixed to the bottom of the
 * viewport, full width, and wraps onto more lines when it has to, so it fits
 * any email and screen. Each app decides when to show it and feeds it from its
 * own AuthService, which also owns the 30-minute timer.
 *
 * Render it at the end of `mat-sidenav-content`: an open sidenav then covers
 * it, and dialogs and menus open above it.
 */
@Component({
  selector: 'jfw-impersonation-bar',
  templateUrl: './impersonation-bar.component.html',
  styleUrl: './impersonation-bar.component.scss',
  imports: [MatButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImpersonationBarComponent {
  /** Who the employee is signed in as. */
  readonly signedInAsEmail = input<string | null | undefined>(null);
  /** The employee impersonating them. */
  readonly impersonatorEmail = input<string | null | undefined>(null);
  /** Milliseconds left in the session. */
  readonly timeLeft = input<number | null | undefined>(null);

  readonly exit = output<void>();

  protected readonly formatTimeLeft = formatTimeLeft;

  private readonly bar = viewChild.required<ElementRef<HTMLElement>>('bar');

  constructor() {
    const root = inject(DOCUMENT).documentElement;
    const destroyRef = inject(DestroyRef);

    // browser only: publish the bar's real height, which changes as it wraps
    afterNextRender(() => {
      const bar = this.bar().nativeElement;
      const publish = () =>
        root.style.setProperty(
          IMPERSONATION_BAR_HEIGHT_VAR,
          `${Math.ceil(bar.getBoundingClientRect().height)}px`,
        );
      publish();

      const observer =
        typeof ResizeObserver === 'undefined'
          ? null
          : new ResizeObserver(publish);
      observer?.observe(bar);

      destroyRef.onDestroy(() => {
        observer?.disconnect();
        root.style.removeProperty(IMPERSONATION_BAR_HEIGHT_VAR);
      });
    });
  }
}
